import { useCallback, useEffect, useRef, useState } from "react";
import type Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";
import { classifyPress, HOLD_MS, takeHoldHint } from "./touchPickup";
import { cancelKonvaDrags } from "./useStagePanZoom";

export interface PickupArm {
  /** Called once the finger has held still for {@link HOLD_MS}. */
  onPickup: () => void;
  /** Finger lifted before the hold completed without panning. */
  onTap?: () => void;
}

interface ArmedPress extends PickupArm {
  key: string;
  touchId: number;
  x: number;
  y: number;
  startedAt: number;
  timer: ReturnType<typeof setTimeout>;
}

/** The touch that started a Konva `touchstart`, or null for mouse/pen. */
export function pressedTouch(e: KonvaEventObject<unknown>): Touch | null {
  const evt = e.evt as TouchEvent | undefined;
  if (!evt || !("changedTouches" in evt) || !evt.changedTouches?.length) return null;
  if (evt.touches && evt.touches.length > 1) return null;
  return evt.changedTouches[0];
}

/**
 * Hand a held finger to a Konva node: drop the Stage's armed pan, make the
 * node draggable, and start its drag on that finger so it follows from here.
 */
export function startNodePickup(node: Konva.Node, touch: Touch, evt: TouchEvent) {
  const stage = node.getStage();
  if (!stage) return;
  cancelKonvaDrags(stage);
  node.draggable(true);
  node.startDrag({ pointerId: touch.identifier, evt });
}

/**
 * Press-and-hold pickup for canvas items that aren't selected (see
 * `touchPickup.ts`). `arm` starts the hold timer for one item; moving past
 * the slop, a second finger, or lifting early disarms it. `liftedKey` is the
 * item currently picked up, for its draggable flag and lift styling; call
 * `drop` when its drag ends.
 *
 * A swipe that started on an item (so it panned instead) flashes the
 * one-time `holdHint`.
 */
export function useTouchPickup() {
  const [liftedKey, setLiftedKey] = useState<string | null>(null);
  const [holdHint, setHoldHint] = useState(false);
  const armRef = useRef<ArmedPress | null>(null);
  const hintTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const disarm = useCallback(() => {
    const a = armRef.current;
    if (a) clearTimeout(a.timer);
    armRef.current = null;
  }, []);

  const arm = useCallback(
    (key: string, touch: Touch, opts: PickupArm) => {
      disarm();
      const timer = setTimeout(() => {
        const a = armRef.current;
        if (!a || a.key !== key) return;
        armRef.current = null;
        setLiftedKey(key);
        navigator.vibrate?.(8);
        a.onPickup();
      }, HOLD_MS);
      armRef.current = {
        ...opts,
        key,
        touchId: touch.identifier,
        x: touch.clientX,
        y: touch.clientY,
        startedAt: performance.now(),
        timer,
      };
    },
    [disarm]
  );

  const drop = useCallback(() => setLiftedKey(null), []);

  useEffect(() => {
    function showHint() {
      if (!takeHoldHint()) return;
      setHoldHint(true);
      if (hintTimer.current) clearTimeout(hintTimer.current);
      hintTimer.current = setTimeout(() => setHoldHint(false), 2400);
    }
    function onStart(e: TouchEvent) {
      if (armRef.current && e.touches.length > 1) disarm();
    }
    function onMove(e: TouchEvent) {
      const a = armRef.current;
      if (!a) return;
      const t = Array.from(e.touches).find((x) => x.identifier === a.touchId);
      const outcome = classifyPress({
        elapsedMs: performance.now() - a.startedAt,
        movedPx: t ? Math.hypot(t.clientX - a.x, t.clientY - a.y) : 0,
        released: false,
        fingers: e.touches.length,
      });
      if (outcome !== "pan") return;
      disarm();
      if (e.touches.length === 1) showHint();
    }
    function onEnd(e: TouchEvent) {
      const a = armRef.current;
      if (!a) return;
      if (!Array.from(e.changedTouches).some((x) => x.identifier === a.touchId)) return;
      disarm();
      if (e.type === "touchend") a.onTap?.();
    }
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd);
    window.addEventListener("touchcancel", onEnd);
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
      window.removeEventListener("touchcancel", onEnd);
      disarm();
      if (hintTimer.current) clearTimeout(hintTimer.current);
    };
  }, [disarm]);

  return { liftedKey, holdHint, arm, disarm, drop };
}
